/**
 * AI Generation Types for OpenSCAD script generation via OpenRouter
 */

/** Mode of AI operation */
export type AiMode = 'create' | 'modify' | 'explain';

/** Current step in the AI generation pipeline */
export type AiStep =
  | 'idle'
  | 'analyze'
  | 'plan'
  | 'planApproval' // User reviews and can request changes to the plan
  | 'implement'
  | 'test'
  | 'bugfix'
  | 'complete'
  | 'error';

/** Result of a single AI step */
export interface AiStepResult {
  step: AiStep;
  content: string;
  tokens: {
    prompt: number;
    completion: number;
  };
  durationMs: number;
}

/** Overall state of the AI generation process */
export interface AiGenerationState {
  mode: AiMode;
  step: AiStep;
  results: AiStepResult[];
  currentContent: string; // Streamed content for current step
  generatedCode: string | null; // Final generated code
  error?: string;
  bugfixAttempts: number;
  compilationError?: string; // Last compilation error if any
  plan?: string; // The generated plan waiting for approval
}

/** Token usage tracking */
export interface AiTokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
}

/** Prompt templates for each step */
export interface AiPromptTemplates {
  analyze: string;
  plan: string;
  implement: string;
  fix: string;
}

/** User AI settings stored in localStorage */
export interface AiSettings {
  apiKey: string;
  defaultModel: string;
  promptTemplates: AiPromptTemplates;
}

/** OpenRouter model information */
export interface OpenRouterModel {
  id: string;
  name: string;
  description?: string;
  pricing: {
    prompt: string; // Cost per token as string (e.g., "0.000001")
    completion: string;
  };
  context_length: number;
  top_provider?: {
    max_completion_tokens?: number;
  };
  architecture?: {
    modality?: string;
    tokenizer?: string;
  };
}

/** Streaming chunk from AI generation */
export interface AiStreamChunk {
  content: string;
  done: boolean;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/** Options for the useAi hook */
export interface UseAiOptions {
  onCodeGenerated?: (code: string) => void;
  onError?: (error: string) => void;
  onStepComplete?: (step: AiStep, result: AiStepResult) => void;
}

/** Return type for the useAi hook */
export interface UseAiReturn {
  // State
  state: AiGenerationState;
  isGenerating: boolean;
  canGenerate: boolean;

  // Token tracking
  tokenUsage: AiTokenUsage;

  // Actions
  generate: (
    mode: AiMode,
    prompt: string,
    model: string,
    existingCode?: string,
  ) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

/** Default values for AI settings */
export const DEFAULT_AI_SETTINGS: Omit<AiSettings, 'promptTemplates'> = {
  apiKey: '',
  defaultModel: 'anthropic/claude-sonnet-4',
};

/** Initial generation state */
export const INITIAL_GENERATION_STATE: AiGenerationState = {
  mode: 'create',
  step: 'idle',
  results: [],
  currentContent: '',
  generatedCode: null,
  bugfixAttempts: 0,
};

/** Maximum number of bugfix attempts */
export const MAX_BUGFIX_ATTEMPTS = 3;

// ---------------------------------------------------------------------------
// Explain mode types
// ---------------------------------------------------------------------------

/** A single message in the explain conversation thread */
export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** State for the explain-with-AI feature */
export interface AiExplainState {
  step: 'idle' | 'explaining' | 'complete' | 'error';
  /** The code snippet the user selected */
  selectedCode: string;
  /** The full editor content (sent as context) */
  contextCode: string;
  /** Accumulated conversation thread */
  messages: AiChatMessage[];
  /** Streaming content for the currently active assistant response */
  currentContent: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Improve mode types
// ---------------------------------------------------------------------------

/** State for the improve-with-AI feature */
export interface AiImproveState {
  step: 'idle' | 'awaiting-input' | 'improving' | 'complete' | 'error';
  /** The code snippet the user selected */
  selectedCode: string;
  /** The full editor content (sent as context) */
  contextCode: string;
  /** The instruction the user typed before submitting */
  userInstruction: string;
  /** Accumulated conversation thread */
  messages: AiChatMessage[];
  /** Streaming content for the currently active assistant response */
  currentContent: string;
  error?: string;
}

// ---------------------------------------------------------------------------

/** Steps in order for progress display */
export const AI_STEPS_ORDER: AiStep[] = [
  'analyze',
  'plan',
  'planApproval',
  'implement',
  'test',
  'bugfix',
];

/** Human-readable step labels */
export const AI_STEP_LABELS: Record<AiStep, string> = {
  idle: 'Ready',
  analyze: 'Analyzing',
  plan: 'Planning',
  planApproval: 'Review Plan',
  implement: 'Implementing',
  test: 'Testing',
  bugfix: 'Fixing',
  complete: 'Complete',
  error: 'Error',
};
